import { Link } from "react-router-dom";
import { MapPin, Truck, Weight, Route, BadgeCheck, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/common";

export function LoadCard({ load, onSave, onView, showActions = true }) {
  return (
    <div data-testid={`load-card-${load.xd_number}`} className="bg-xd-paper border border-xd-border rounded-lg p-5 hover:border-xd-orange/50 transition-colors flex flex-col animate-fade-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-xd-dim">{load.xd_number}</span>
            {load.sample && <span className="text-[10px] font-bold uppercase tracking-wide text-xd-gold bg-xd-gold/10 border border-xd-gold/30 rounded px-1.5 py-0.5">Sample</span>}
            {load.xd_exclusive && <span className="text-[10px] font-bold uppercase tracking-wide text-xd-orange bg-xd-orange/10 border border-xd-orange/30 rounded px-1.5 py-0.5">XD Exclusive</span>}
          </div>
          <h3 className="mt-1.5 font-heading text-lg font-bold leading-tight">{load.origin_city}, {load.origin_state} <span className="text-xd-dim">→</span> {load.dest_city}, {load.dest_state}</h3>
        </div>
        {load.status && load.status !== "Available" ? <StatusPill status={load.status} /> :
          <span className="inline-flex items-center gap-1 text-xs text-xd-green"><BadgeCheck size={14} /> Verified</span>}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
        <Info icon={MapPin} label="Pickup" value={load.pickup_date} />
        <Info icon={Truck} label="Equipment" value={`${load.trailer_type} · ${load.trailer_length}ft`} />
        <Info icon={Weight} label="Weight" value={`${load.weight?.toLocaleString()} lbs`} />
        <Info icon={Route} label="Miles" value={`${load.loaded_miles} + ${load.deadhead_miles} DH`} />
      </div>

      <div className="mt-4 pt-4 border-t border-xd-border flex items-end justify-between">
        <div>
          <p className="font-heading text-2xl font-black text-white">${load.total_pay?.toLocaleString()}</p>
          <p className="text-xs text-xd-blue font-semibold">${load.rate_per_mile}/mi · {load.load_source}</p>
        </div>
        {load.match_score != null && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-xd-dim">XD Match</p>
            <p className="font-mono font-bold text-xd-green">{load.match_score}%</p>
          </div>
        )}
      </div>

      {showActions && (
        <div className="mt-4 flex gap-2">
          <Button asChild data-testid={`view-load-${load.xd_number}`} className="flex-1 bg-xd-orange hover:bg-xd-orangeH text-white font-semibold">
            <Link to={`/find-loads/${load.id}`}>View Load</Link>
          </Button>
          {onSave && (
            <Button data-testid={`save-load-${load.xd_number}`} variant="outline" className="border-xd-border text-white hover:bg-xd-surface" onClick={() => onSave(load)}>
              <Bookmark size={16} className={load.saved ? "fill-xd-orange text-xd-orange" : ""} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={15} className="text-xd-dim mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-xd-dim">{label}</p>
        <p className="text-white font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
